import { Users, MessageCircle, Clock, User } from "lucide-react"
import { AnimatedSection } from "./AnimatedSection"
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "./Card"
import { Badge } from "./Badge"
import { FallbackImage } from "./FallbackImage"

export function PanelDiscussionSection({ currentEdition = "vizag" }) {
  // Define content for each edition
  const editionContent = {
    vizag: {
      title: "Building Impactful Careers in Wi-Fi Industry",
      description: "Interactive discussion on building impactful careers in the Wi-Fi industry",
      subtitle: "Interactive Career Discussion",
      detailDescription: "Join our expert panel as they share insights on career development, industry trends, and opportunities in the wireless technology sector.",
      image: "/images/panel_discussion.png",
      participants: [
        { name: "Sitarama", role: "Moderator", isModerator: true },
        { name: "Srikanth Subramanian", role: "Panelist", isModerator: false },
        { name: "Rasika Nayanajith", role: "Panelist", isModerator: false },
        { name: "Sashank Tadakamadla", role: "Panelist", isModerator: false },
        { name: "Shivam Thakur", role: "Panelist", isModerator: false },
        { name: "Bhuvnesh Sachdeva", role: "Panelist", isModerator: false }
      ]
    },
    bangalore: {
      title: "The Role of AI in Modern Wi-Fi",
      description: "Interactive discussion on the transformative role of artificial intelligence in Wi-Fi networks",
      subtitle: "AI & Future of Wireless",
      detailDescription: "Join our expert panelists as they explore how artificial intelligence is revolutionizing modern Wi-Fi networks, from network optimization to predictive analytics.",
      image: "/images/panel_discussion_banglore.jpeg",
      participants: [
        { name: "Sainath P", role: "Moderator", isModerator: true },
        { name: "Ashutosh Datta", role: "Panelist", isModerator: false },
        { name: "Asvin Kumar", role: "Panelist", isModerator: false },
        { name: "Charumathi Iyer", role: "Panelist", isModerator: false },
        { name: "Prabhash Dhyani", role: "Panelist", isModerator: false }
      ]
    },
    chennai: {
      title: "NRAI — Not Replaceable by AI",
      description: "Building a career in Wi-Fi that thrives in the age of AI",
      subtitle: "Building a Career in Wi-Fi That Thrives in the Age of AI",
      detailDescription: "AI is reshaping every layer of wireless — from RF tuning and protocol design to test, deployment, and support. So where does that leave the engineer? This panel goes beyond the buzzwords to explore the skills, mindset, and craft that keep Wi-Fi professionals not just relevant, but indispensable. Expect honest conversations about what AI can take over, what it absolutely cannot, and how to position yourself on the right side of that line for the decade ahead.",
      image: "/images/chennaiCTS2026-paneldiscussion.jpg",
      videoId: "iAdOOWF35a4",
      panelistsTBA: false,
      participants: [
        { name: "Sitarama Penumetsa", role: "Moderator", isModerator: true },
        { name: "Susinder Gulasekaran", role: "Panelist", isModerator: false },
        { name: "Rasika Nayanajith", role: "Panelist", isModerator: false },
        { name: "Sudarsan Vasudevan", role: "Panelist", isModerator: false },
        { name: "Vishnu Kumar", role: "Panelist", isModerator: false }
      ]
    }
  }

  const content = editionContent[currentEdition]

  return (
    <section id="panel-discussion" className="py-20 bg-gradient-to-br from-green-50 to-white">
      <div className="container mx-auto px-4">
        <AnimatedSection>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Panel Discussion</h2>
            <p className="text-xl text-green-600 max-w-2xl mx-auto">
              {content.description}
            </p>
          </div>
        </AnimatedSection>

        <div className="max-w-6xl mx-auto">
          <AnimatedSection delay={300}>
            <Card className="group hover:shadow-xl transition-all duration-300 border-green-100 hover:border-green-200 overflow-hidden">
              {currentEdition === "chennai" ? (
                <div className="flex flex-col">
                  {content.videoId ? (
                    <div className="w-full bg-black">
                      <iframe
                        src={`https://www.youtube.com/embed/${content.videoId}`}
                        title={`Panel Discussion - ${content.title}`}
                        loading="lazy"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="aspect-video w-full"
                      ></iframe>
                    </div>
                  ) : (
                    <div className="relative w-full bg-gradient-to-br from-green-50 to-white flex items-center justify-center">
                      <FallbackImage
                        src={content.image}
                        alt={`Panel Discussion - ${content.title}`}
                        className="w-full h-auto max-h-[600px] object-contain"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                    </div>
                  )}

                  <div className="flex flex-col">
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <Badge className="bg-blue-100 text-blue-700 mb-3">Panel Discussion</Badge>
                          <CardTitle className="text-green-800 group-hover:text-green-600 transition-colors leading-tight text-xl md:text-2xl">
                            {content.title}
                          </CardTitle>
                          <CardDescription className="text-green-600 font-medium mt-1">
                            {content.subtitle}
                          </CardDescription>
                        </div>
                        <MessageCircle className="w-5 h-5 text-green-400 group-hover:text-green-600 transition-colors flex-shrink-0 mt-1" />
                      </div>
                    </CardHeader>

                    <CardContent className="flex-1 pt-0">
                      <p className="text-green-700 leading-relaxed mb-4 text-sm md:text-base">
                        {content.detailDescription}
                      </p>

                      <div className="space-y-3 mb-4">
                        <div className="flex flex-wrap items-center gap-3 text-xs text-green-600">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>Interactive Session</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            <span>Q&A Included</span>
                          </div>
                        </div>

                        <div>
                          <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-1 text-sm">
                            <User className="w-3 h-3" />
                            Panellist
                          </h4>
                          {content.panelistsTBA ? (
                            <div className="flex items-center gap-2 rounded-lg border border-dashed border-green-300 bg-green-50/60 px-3 py-2">
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                              </span>
                              <span className="text-green-700 text-xs">
                                <strong>Panelists to be announced</strong> — stay tuned for the lineup
                              </span>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-2">
                              {content.participants.map((participant, index) => (
                                <div key={index} className="flex items-center gap-1">
                                  <div className={`w-1.5 h-1.5 ${participant.isModerator ? 'bg-blue-500' : 'bg-green-500'} rounded-full`}></div>
                                  <span className={`${participant.isModerator ? 'text-blue-700' : 'text-green-700'} text-xs`}>
                                    <strong>{participant.name}</strong> {participant.isModerator && '(Moderator)'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-auto pt-3 border-t border-green-100">
                        <p className="text-green-600 font-medium text-xs">
                          {content.videoId
                            ? "Missed it on the day? Watch the full panel above."
                            : "Don't miss this opportunity to gain valuable insights from industry experts!"}
                        </p>
                      </div>
                    </CardContent>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col lg:flex-row h-auto">
                  <div className="lg:w-1/2 relative flex items-center justify-center bg-gradient-to-br from-green-50/30 to-white/50">
                    <div className="w-full h-full relative">
                      <FallbackImage
                        src={content.image}
                        alt={`Panel Discussion - ${content.title}`}
                        className="w-full h-full object-cover rounded-none"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </div>

                  <div className="lg:w-1/2 flex flex-col">
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <Badge className="bg-blue-100 text-blue-700 mb-3">Panel Discussion</Badge>
                          <CardTitle className="text-green-800 group-hover:text-green-600 transition-colors leading-tight text-lg">
                            {content.title}
                          </CardTitle>
                          <CardDescription className="text-green-600 font-medium mt-1">
                            {content.subtitle}
                          </CardDescription>
                        </div>
                        <MessageCircle className="w-5 h-5 text-green-400 group-hover:text-green-600 transition-colors flex-shrink-0 mt-1" />
                      </div>
                    </CardHeader>

                    <CardContent className="flex-1 pt-0">
                      <p className="text-green-700 leading-relaxed mb-4 text-sm">
                        {content.detailDescription}
                      </p>

                      <div className="space-y-3 mb-4">
                        <div className="flex flex-wrap items-center gap-3 text-xs text-green-600">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>Interactive Session</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            <span>Q&A Included</span>
                          </div>
                        </div>

                        <div>
                          <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-1 text-sm">
                            <User className="w-3 h-3" />
                            Panellist
                          </h4>
                          {content.panelistsTBA ? (
                            <div className="flex items-center gap-2 rounded-lg border border-dashed border-green-300 bg-green-50/60 px-3 py-2">
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                              </span>
                              <span className="text-green-700 text-xs">
                                <strong>Panelists to be announced</strong> — stay tuned for the lineup
                              </span>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-2">
                              {content.participants.map((participant, index) => (
                                <div key={index} className="flex items-center gap-1">
                                  <div className={`w-1.5 h-1.5 ${participant.isModerator ? 'bg-blue-500' : 'bg-green-500'} rounded-full`}></div>
                                  <span className={`${participant.isModerator ? 'text-blue-700' : 'text-green-700'} text-xs`}>
                                    <strong>{participant.name}</strong> {participant.isModerator && '(Moderator)'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-auto pt-3 border-t border-green-100">
                        <p className="text-green-600 font-medium text-xs">
                          Don't miss this opportunity to gain valuable insights from industry experts!
                        </p>
                      </div>
                    </CardContent>
                  </div>
                </div>
              )}
            </Card>
          </AnimatedSection>
        </div>
      </div>
    </section>
  )
}